import { createFileRoute, Link } from "@tanstack/react-router";
import { Layout } from "@/components/Layout";
import { leafHead } from "@/lib/seo/leaf-head";
import { SITE_URL } from "@/lib/seo";
import {
  ALL_TOOLS,
  CLUSTERS as TOOL_CLUSTERS,
  CLUSTER_EMOJI,
  CLUSTER_LABEL,
  toolsByCluster,
  NEW_TOOLS,
  NEW_TOOL_CLUSTERS,
} from "@/lib/utility-tools/registry";
import { CLUSTERS as BMI_CLUSTERS, pathsByCluster, type ClusterId } from "@/lib/bmi/clusters";

type Item = { path: string; label: string; note?: string };
type Group = { id: string; emoji: string; title: string; blurb: string; items: Item[] };

const GOLD: Item[] = [
  { path: "/today-gold-rate-calculator-pakistan", label: "Today Gold Rate Calculator (Pakistan)", note: "Tola / gram / 10g in PKR" },
  { path: "/today-gold-rate-calculator-india", label: "Today Gold Rate Calculator (India)", note: "10g / gram in INR" },
  { path: "/tools/gold-purity-converter", label: "Gold Purity Converter", note: "24K · 22K · 21K · 18K · 14K" },
  { path: "/gold-purity-calculator", label: "Gold Purity Calculator", note: "Karat to pure-gold weight" },
  { path: "/tools/jewelry-making-estimator", label: "Jewelry Making Cost Estimator", note: "Making + wastage + tax" },
  { path: "/jewelry-wastage-calculator", label: "Jewelry Wastage Calculator", note: "Wastage % impact" },
  { path: "/tools/historical-roi-calculator", label: "Gold Historical ROI Calculator", note: "Back-test any year" },
  { path: "/gold-investment-calculator", label: "Gold Investment Calculator", note: "Profit / loss projection" },
  { path: "/zakat-calculator", label: "Zakat Calculator", note: "Gold + silver + cash nisab" },
  { path: "/pakistan/zakat-nisab", label: "Pakistan Zakat Nisab Calculator", note: "Live nisab threshold" },
];

const MONEY: Item[] = [
  { path: "/prize-bond", label: "Prize Bond Checker", note: "All denominations" },
];

const AGE: Item[] = [
  { path: "/age-calculator", label: "Age Calculator (Main)", note: "Years, months, days, hours" },
  { path: "/age-calculator/chronological-date-difference", label: "Date Difference Calculator" },
  { path: "/age-calculator/gestational-pregnancy", label: "Gestational Age Calculator" },
  { path: "/age-calculator/milestone-birthday", label: "Milestone Birthday Countdown" },
  { path: "/age-calculator/retirement-countdown", label: "Retirement Countdown" },
  { path: "/age-calculator/retirement-pension", label: "Pension Age Calculator" },
  { path: "/age-calculator/chinese-lunar-age", label: "Chinese Lunar Age" },
  { path: "/age-calculator/lifestyle-lunar-face", label: "Lunar & Face Age" },
  { path: "/age-calculator/biological-health-bmi", label: "Biological Age (Health + BMI)" },
  { path: "/age-calculator/relationship-age-gap", label: "Relationship Age Gap" },
  { path: "/age-calculator/pet-age-converter", label: "Pet Age Converter" },
  { path: "/age-calculator/pet-dog-cat", label: "Dog & Cat Age Calculator" },
  { path: "/age-calculator/school-admission", label: "School Admission Age" },
  { path: "/age-calculator/upsc-eligibility", label: "UPSC Eligibility Age" },
  { path: "/age-calculator/fpsc-ppsc-jobs", label: "FPSC / PPSC Job Age Limit" },
  { path: "/age-calculator/military-bharti", label: "Army & Bharti Age Limit" },
  { path: "/age-calculator/passport-visa-form", label: "Passport & Visa Form Age" },
];

const PREGNANCY: Item[] = [
  { path: "/pregnancy-calculator-week-12", label: "Pregnancy Week Calculator", note: "Week-by-week tracker" },
  { path: "/conception-calculator-week-8", label: "Conception Date Calculator" },
  { path: "/twin-pregnancy-calculator-week-20", label: "Twin Pregnancy Calculator" },
  { path: "/ivf-pregnancy-calculator-week-10", label: "IVF Pregnancy Calculator" },
  { path: "/pregnancy-countdown-calculator-10-week-left", label: "Pregnancy Countdown Calculator" },
];

export const Route = createFileRoute("/calculators")({
  head: () =>
    leafHead({
      path: "/calculators",
      title: "All Calculators Hub — 120+ Free Online Calculators | DrHint",
      description:
        "One directory for every free DrHint calculator: gold rate, zakat, loan EMI, BMI by age & height, pregnancy week, age, and 80 utility calculators. No sign-up.",
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
              { "@type": "ListItem", position: 2, name: "Calculators Hub", item: `${SITE_URL}/calculators` },
            ],
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "DrHint Calculators Hub",
            url: `${SITE_URL}/calculators`,
            description: "Category-wise directory of every free calculator on DrHint.",
            mainEntity: {
              "@type": "ItemList",
              itemListOrder: "https://schema.org/ItemListOrderAscending",
              numberOfItems: GOLD.length + MONEY.length + AGE.length + PREGNANCY.length + ALL_TOOLS.length,
              itemListElement: [
                ...[...GOLD, ...MONEY, ...AGE, ...PREGNANCY].map((it, i) => ({
                  "@type": "ListItem",
                  position: i + 1,
                  name: it.label,
                  url: `${SITE_URL}${it.path}`,
                })),
                ...ALL_TOOLS.map((t, i) => ({
                  "@type": "ListItem",
                  position: GOLD.length + MONEY.length + AGE.length + PREGNANCY.length + i + 1,
                  name: t.name,
                  url: `${SITE_URL}/tools/${t.slug}`,
                })),
              ],
            },
          }),
        },
      ],
    }),
  component: CalculatorsHub,
});

function CalculatorsHub() {
  const bmi = pathsByCluster();
  const bmiOrder: ClusterId[] = ["A", "B", "C", "D"];

  const groups: Group[] = [
    { id: "gold", emoji: "🥇", title: "Gold, Silver & Zakat Calculators", blurb: "Live-rate powered precious-metal maths — purity, making cost, ROI and zakat nisab.", items: GOLD },
    { id: "money", emoji: "💰", title: "Money & Loan Calculators", blurb: "EMI planning, prize bond checking and money planning.", items: MONEY },
    { id: "age", emoji: "🎂", title: "Age & Date Calculators", blurb: "17 age tools — exact age, eligibility limits, retirement and pet age.", items: AGE },
    { id: "pregnancy", emoji: "🤰", title: "Pregnancy & Conception Calculators", blurb: "Week-by-week due date, twin and IVF pregnancy tracking.", items: PREGNANCY },
  ];

  return (
    <Layout>
      <section className="mx-auto max-w-6xl px-4 py-8">
        <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground">
          <Link to="/" className="hover:text-primary">Home</Link> <span className="mx-1">/</span> Calculators Hub
        </nav>

        <h1 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">
          Calculators Hub — Every DrHint Calculator in One Place
        </h1>
        <p className="mt-3 max-w-3xl text-sm text-muted-foreground">
          A single, crawlable directory of every calculator on DrHint — gold and zakat, loan EMI, BMI by age
          and height, pregnancy weeks, age eligibility, plus {ALL_TOOLS.length} everyday utility calculators.
          All of them run instantly in your browser, free and without sign-up.
        </p>

        <div className="mt-5 flex flex-wrap gap-2 text-xs">
          {[...groups.map((g) => ({ id: g.id, label: `${g.emoji} ${g.title.split(" ")[0]}` })), { id: "bmi", label: "⚖️ BMI" }, { id: "utility", label: "🧰 Utility Tools" }, { id: "new-tools", label: "🆕 New Tools" }].map((c) => (
            <a key={c.id} href={`#${c.id}`} className="rounded-full border border-border bg-card px-3 py-1.5 font-medium hover:border-primary/60">
              {c.label}
            </a>
          ))}
        </div>

        {groups.map((g) => (
          <section key={g.id} id={g.id} className="mt-10 scroll-mt-24">
            <h2 className="text-xl font-bold">{g.emoji} {g.title}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{g.blurb}</p>
            <ul className="mt-4 grid list-none gap-3 pl-0 sm:grid-cols-2 lg:grid-cols-3">
              {g.items.map((it) => (
                <li key={it.path}>
                  <Link
                    to={it.path as never}
                    className="block h-full rounded-2xl border border-border bg-card p-4 transition hover:border-primary/60"
                  >
                    <span className="text-sm font-bold">{it.label}</span>
                    {it.note ? <span className="mt-1 block text-xs text-muted-foreground">{it.note}</span> : null}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        ))}

        <section id="bmi" className="mt-10 scroll-mt-24">
          <h2 className="text-xl font-bold">⚖️ BMI Calculators (by age, height &amp; body type)</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Start at the <Link to="/bmi-calculator" className="text-primary underline">main BMI calculator</Link>, or jump
            straight into an age- or height-specific page.
          </p>
          {bmiOrder.map((id) => {
            const meta = BMI_CLUSTERS[id];
            const paths = bmi[id];
            return (
              <div key={id} className="mt-5 rounded-2xl border border-border bg-card p-4">
                <h3 className="text-sm font-bold">{meta.emoji} {meta.label}</h3>
                <p className="mt-1 text-xs text-muted-foreground">{meta.description}</p>
                <ul className="mt-3 grid list-none grid-cols-2 gap-2 pl-0 sm:grid-cols-4 lg:grid-cols-6">
                  {paths.slice(0, 18).map((p) => (
                    <li key={p}>
                      <Link to={p as never} className="block rounded-md border border-border bg-background px-2 py-1.5 text-xs hover:border-primary/60">
                        {bmiLabel(p, id)}
                      </Link>
                    </li>
                  ))}
                </ul>
                <Link to="/bmi-calculator" className="mt-3 inline-block text-xs font-semibold text-primary">
                  See all {paths.length} {meta.shortLabel} pages →
                </Link>
              </div>
            );
          })}
        </section>

        <section id="utility" className="mt-10 scroll-mt-24">
          <h2 className="text-xl font-bold">🧰 Utility &amp; Everyday Calculators ({ALL_TOOLS.length})</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Health, money, date, creator and home calculators — each with its own page, formula breakdown and FAQs.
          </p>
          {TOOL_CLUSTERS.map((c) => (
            <div key={c} className="mt-5">
              <h3 className="text-sm font-bold">{CLUSTER_EMOJI[c]} {CLUSTER_LABEL[c]}</h3>
              <ul className="mt-3 grid list-none gap-2 pl-0 sm:grid-cols-2 lg:grid-cols-3">
                {toolsByCluster(c).map((t) => (
                  <li key={t.slug}>
                    <Link
                      to="/tools/$tool"
                      params={{ tool: t.slug }}
                      className="block rounded-xl border border-border bg-card px-3 py-2 text-xs font-medium hover:border-primary/60"
                    >
                      {t.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <Link to="/tools/all" className="mt-4 inline-block text-xs font-semibold text-primary">
            Open the full tools directory →
          </Link>
        </section>

        <section id="new-tools" className="mt-12 scroll-mt-24 rounded-3xl border border-primary/30 bg-primary/5 p-5">
          <h2 className="text-xl font-bold">🆕 New Tools ({NEW_TOOLS.length})</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Freshly added calculators — home renovation &amp; trade math, automotive and towing, off-grid power
            and water, culinary &amp; craft, homestead, tech gear and niche business money tools. Direct access
            below.
          </p>
          {NEW_TOOL_CLUSTERS.map((c) => (
            <div key={c} className="mt-5">
              <h3 className="text-sm font-bold">{CLUSTER_EMOJI[c]} {CLUSTER_LABEL[c]}</h3>
              <ul className="mt-3 grid list-none gap-2 pl-0 sm:grid-cols-2 lg:grid-cols-3">
                {toolsByCluster(c).map((t) => (
                  <li key={t.slug}>
                    <Link
                      to="/tools/$tool"
                      params={{ tool: t.slug }}
                      className="block rounded-xl border border-border bg-card px-3 py-2 text-xs font-medium hover:border-primary/60"
                    >
                      {t.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </section>
      </section>
    </Layout>
  );
}

function bmiLabel(p: string, cluster: ClusterId): string {
  const s = p.replace(/^\//, "");
  if (cluster === "C") {
    const m = s.match(/for-(\d+)-feet-(\d+)-inches-tall/);
    return m ? `${m[1]}′${m[2]}″` : s;
  }
  const m = s.match(/(\d+)-years-old/);
  if (!m) return s;
  if (cluster === "B") return `Women ${m[1]}`;
  if (cluster === "D") return `S. Asian ${m[1]}`;
  return `Age ${m[1]}`;
}
